import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Partition } from '@classes/simulation/partition';
import { Subscription } from 'rxjs';
import Chart, { ChartOptions, ChartData, ChartDataSets } from 'chart.js';
import { FormatService } from '@services/format.service';
import { SimulationService } from '@services/simulation.service';
import { TranslateService } from '@ngx-translate/core';
import { CalculatorService } from '@services/calculator.service';
import { ApiText } from '@classes/api-text';

interface GraphTranslations {
    datasets: {
        originalAmount: ApiText;
        editedAmount: ApiText;
    };
    axes: {
        amount: ApiText;
        years: ApiText;
    };
}

@Component({
    selector: 'app-partition-cursors-chart-mjp',
    templateUrl: './partition-cursors-chart-mjp.component.html',
    styleUrls: [ './partition-cursors-chart-mjp.component.scss' ]
})
export class PartitionCursorsChartMjpComponent implements OnInit, OnDestroy {
    @Input() partition: Partition;

    private selectedPartSub: Subscription;
    private simulationValSub: Subscription;
    private projRateSub: Subscription;

    chart: Chart;
    chartOptions: ChartOptions;
    chartData: ChartData;

    chartColor = '#c30045';
    chartParamColor = '#d54c7d';

    translations: GraphTranslations;

    // Used on chartOptions to improve visualization
    minValue: number;
    maxValue: number;

    constructor(
        private formatSrv: FormatService,
        private simulationSrv: SimulationService,
        private translateSrv: TranslateService
    ) {}

    ngOnInit() {
        this.initSubscriptions();

        this.translateSrv.get('partitions.projectionGraph.graph').subscribe((res: GraphTranslations) => {
            this.translations = res;
            this.setChart();
        });
    }

    private initSubscriptions(): void {
        this.selectedPartSub = this.simulationSrv.selectedPartitionChanged.subscribe(selectedPartition => {
            this.partition = selectedPartition;
            this.setChart();
        });

        this.simulationValSub = this.simulationSrv.simulationValChanged.subscribe(simulationValues => {
            this.partition.simulationValues = simulationValues;
            this.setChart();
        });

        this.projRateSub = this.simulationSrv.projectionRateChanged.subscribe(projectionRate => {
            this.partition.projectionRate = projectionRate;
            this.setChart();
        });
    }

    // calculate charts values with projection rate and cursors values
    private setChart(): void {
        // Init array with last executed year (begin at 0)
        const labelsArray: string[] = [];
        const editedAmounts: number[] = [];
        const originalAmounts: number[] = [];

        this.minValue = this.maxValue = null;

        this.partition.simulationValues.years.map((year, i, years) => {
            if (!year.isEstimated) {
                return year;
            }

            const min = year.amount <= year.originalAmount ? year.amount : year.originalAmount;
            const max = year.amount >= year.originalAmount ? year.amount : year.originalAmount;

            if (this.minValue == null || this.minValue > min) {
                this.minValue = min;
            }
            if (this.maxValue == null || this.maxValue < max) {
                this.maxValue = max;
            }

            labelsArray.push(year.year.toString());
            editedAmounts.push(year.amount);
            originalAmounts.push(year.originalAmount);
        });

        // Used on chartOptions to improve visualization
        this.minValue -= 5000;
        this.maxValue += 5000;

        this.initChart(labelsArray, editedAmounts, originalAmounts);
        this.createChart();
    }

    // create charts data objects
    private initChart(labels: string[], editedAmounts: number[], originalAmounts: number[]): void {
        this.chartData = {
            labels: labels,
            datasets: [
                {
                    label: this.translations.datasets.editedAmount.shortLabel,
                    data: editedAmounts,
                    borderColor: this.chartColor,
                    backgroundColor: this.chartColor,
                    pointBackgroundColor: this.chartColor,
                    borderWidth: 2,
                    fill: false,
                    lineTension: 0
                },
                {
                    label: this.translations.datasets.originalAmount.shortLabel,
                    data: originalAmounts,
                    borderColor: this.chartParamColor,
                    backgroundColor: '#FFF',
                    pointBackgroundColor: this.chartParamColor,
                    borderDash: [ 8, 8 ],
                    borderWidth: 2,
                    fill: false,
                    lineTension: 0
                }
            ]
        };

        this.chartOptions = {
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: false,
                            callback: (value: number) => {
                                return this.formatValue(value);
                            },
                            suggestedMin: this.minValue,
                            suggestedMax: this.maxValue,
                            maxTicksLimit: 6
                        },
                        scaleLabel: {
                            display: true,
                            labelString: this.translations.axes.amount.shortLabel
                        }
                    }
                ],
                xAxes: [
                    {
                        scaleLabel: {
                            display: true,
                            labelString: this.translations.axes.years.shortLabel
                        }
                    }
                ]
            },
            tooltips: {
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset: ChartDataSets = data.datasets[tooltipItem.datasetIndex];
                        const value: number = dataset.data[tooltipItem.index] as number;
                        const formattedValue = this.formatValue(value);

                        return `${dataset.label}: ${formattedValue}`;
                    }
                }
            }
        };
    }

    private createChart(): void {
        if (!this.chart) {
            this.chart = new Chart('canvas', {
                type: 'line',
                data: this.chartData,
                options: this.chartOptions
            });
        } else {
            this.chart.data = this.chartData;
            this.chart.config.options.scales.yAxes[0].ticks.suggestedMin = this.minValue;
            this.chart.config.options.scales.yAxes[0].ticks.suggestedMax = this.maxValue;
            this.chart.update(0);
        }
    }

    private formatValue(value: number): string {
        if (Math.abs(value) < 10) {
            return this.formatSrv.formatAmount(value, 'fixed', 1) + ' €';
        }
        if (Math.abs(value) < 1000) {
            return this.formatSrv.formatAmount(value);
        }
        return this.formatSrv.formatAmount(value, 'fixed', 0.001) + ' K€';
        // return this.formatSrv.formatAmount(value, 'auto');
    }

    ngOnDestroy() {
        if (this.selectedPartSub) {
            this.selectedPartSub.unsubscribe();
        }

        if (this.simulationValSub) {
            this.simulationValSub.unsubscribe();
        }

        if (this.projRateSub) {
            this.projRateSub.unsubscribe();
        }
    }
}
